import { LdInputCustomEvent } from "@emdgroup-liquid/liquid";
import { LdIcon, LdInput } from "@liquid/react";
import { KeyboardEventHandler, useRef, useState } from "react";
import { useClickAway, useDebounce } from "react-use";
import { StyledSearchAutocomplete } from "./AutoComplete.style";
import { AutocompleteMenu } from "./AutocompleteMenu";
import { createAutocompleteDisplayValue } from "@src/utils/utils";

type AutocompleteProps = {
  placeholder?: string;
  suggestions?: any[];
  onChange?: (term?: string | any) => void;
  onChangeDebounced?: (term?: string) => void;
  value?: string;
  onSelectSuggestion?: (suggestion?: string | any) => void;
  selectedSuggestion?: string;
  disabled?: boolean;
  min?: number;
  isLoading?: boolean;
};

function AutoComplete({
  suggestions,
  onChange,
  onChangeDebounced,
  value,
  placeholder,
  onSelectSuggestion,
  disabled,
  selectedSuggestion,
  min = 1,
  isLoading,
}: AutocompleteProps) {
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLLdInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  useClickAway(autocompleteRef, () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  });



  useDebounce(
    () => {
      if (value && min <= value.length) onChangeDebounced?.(value);
    },
    300,
    [value]
  );

  const handleMenuOpen = () => {
    if (value && value.length >= min && (isLoading || (suggestions && suggestions.length > 0))) {
      setIsOpen(true);
    }
  };


  const selectSuggestion = (suggestion?: any) => {
    onChange?.(
      suggestion ? createAutocompleteDisplayValue(suggestion) : ""
    );
    onSelectSuggestion?.(suggestion);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const onInputChange = ({ target: { value } }: LdInputCustomEvent<any>) => {
    onChange?.(value);
    setIsOpen(!!value && value.length >= min);
    setFocusedIndex(-1);
  };

  const onInputFocus = () => {
    setFocusedIndex(-1);
  };

  const onInputKeyDown: KeyboardEventHandler<HTMLLdInputElement> = (e) => {
    if (e.key === "Escape" || e.key === "Tab") {
      setIsOpen(false);
      setFocusedIndex(-1);
      return;
    }

    if (!suggestions?.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setIsOpen(true);
      setFocusedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (focusedIndex >= 0 && suggestions[focusedIndex]) {
        selectSuggestion(suggestions[focusedIndex]);
      } else {
        selectSuggestion(value);
      }
    }
  };


  return (
    <StyledSearchAutocomplete ref={autocompleteRef} style={{ position: "relative" }}>
      <LdInput
        data-testid="autocomplete-input"
        ref={searchInputRef}
        value={value}
        placeholder={placeholder}
        onLdinput={onInputChange}
        onFocus={onInputFocus}
        onKeyDown={onInputKeyDown}
        onClick={handleMenuOpen}
        size="lg"
        disabled={disabled}
      >
        <LdIcon
          name="magnifier"
          slot="start"
          onClick={() => selectSuggestion(value)}
        />
        <LdIcon
          name="cross"
          slot="end"
          onClick={(e) => {
            e.stopPropagation();
            selectSuggestion(undefined);
          }}
        />
      </LdInput>

      <AutocompleteMenu
        searchInputRef={searchInputRef}
        suggestions={suggestions}
        selectSuggestion={selectSuggestion}
        isLoading={isLoading}
        isOpen={isOpen}
        focusedIndex={focusedIndex}
        inputValue={value}
        min={min}
      />
    </StyledSearchAutocomplete>
  );
}

export { AutoComplete };
